/**
 * Deterministic ledger (Xero / MYOB / GL) spreadsheet parser for vouching
 * (MOR-574).
 *
 * GL exports are grouped-by-account reports — a title block, a header row, then
 * per-account sections (an account-name header row, its transaction rows, a
 * "Total <account>" / "Net movement" subtotal). There is no flat table and no
 * bank account number. We only need {date, amount, description, account} per
 * line to match against bank transactions on date + amount.
 *
 * `extractLedgerRowsFromMatrix` is the pure, unit-tested core (no xlsx
 * dependency). `parseLedgerWorkbook` is the thin xlsx wrapper used in the
 * browser. Parsing is intentionally tolerant: unknown layouts yield zero rows
 * (the caller surfaces "couldn't parse this file") rather than throwing.
 */

import * as XLSX from "xlsx";
import { normalizeDate } from "@cvx/jobs/summarization/agents/bankTransactions/dedupCore";
import type { LedgerRow } from "@cvx/jobs/summarization/agents/bankTransactions/vouchCore";

// Column header aliases (lowercased, exact-after-trim match against header row).
const HEADER_ALIASES = {
  date: ["date", "transaction date", "txn date", "posted date"],
  debit: ["debit", "dr", "debit amount"],
  credit: ["credit", "cr", "credit amount"],
  amount: ["amount", "value", "net", "net amount"],
  description: [
    "description",
    "narration",
    "details",
    "particulars",
    "memo",
    "payee",
    "name",
  ],
  reference: ["reference", "ref", "invoice", "invoice number"],
  // NB: a Xero GL "Source" column is the transaction type ("Spend Money"), not
  // the account — the account is the section header, tracked separately below.
  account: ["account", "account name", "account code"],
} as const;

// Rows whose leading text marks a subtotal / balance line — never transactions.
const SUBTOTAL_PREFIXES = [
  "total",
  "net movement",
  "opening balance",
  "closing balance",
  "balance c/f",
  "balance b/f",
  "balance carried",
  "balance brought",
  "subtotal",
];

/**
 * Formats a Date using its LOCAL components. xlsx parses date cells into Date
 * objects at local midnight; `toISOString()` would shift them to UTC and turn
 * e.g. 2 Sep into 1 Sep in +10/+11 timezones — an off-by-one that breaks every
 * match. Local components avoid that.
 */
function dateToLocalIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function cellToString(cell: unknown): string {
  if (cell == null) return "";
  if (cell instanceof Date) return dateToLocalIso(cell);
  return String(cell).trim();
}

/** Coerces a cell to ISO date, or null if it isn't a recognisable date. */
export function coerceDateToIso(cell: unknown): string | null {
  if (cell instanceof Date && !isNaN(cell.getTime())) {
    return dateToLocalIso(cell);
  }
  const s = cellToString(cell);
  if (!s) return null;
  const norm = normalizeDate(s);
  return /^\d{4}-\d{2}-\d{2}$/.test(norm) ? norm : null;
}

/** Coerces a cell to a number, handling "$1,234.50", "(123.00)" → -123, "". */
export function coerceMoney(cell: unknown): number | null {
  if (typeof cell === "number") return isFinite(cell) ? cell : null;
  const raw = cellToString(cell);
  if (!raw) return null;
  const negative = /^\(.*\)$/.test(raw);
  const cleaned = raw.replace(/[(),$\s]/g, "").replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = parseFloat(cleaned);
  if (!isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
}

function matchHeader(
  cells: string[],
): Record<keyof typeof HEADER_ALIASES, number> | null {
  const lower = cells.map((c) => c.toLowerCase());
  const indexOfAlias = (aliases: readonly string[]): number =>
    lower.findIndex((c) => aliases.includes(c));

  const cols = {
    date: indexOfAlias(HEADER_ALIASES.date),
    debit: indexOfAlias(HEADER_ALIASES.debit),
    credit: indexOfAlias(HEADER_ALIASES.credit),
    amount: indexOfAlias(HEADER_ALIASES.amount),
    description: indexOfAlias(HEADER_ALIASES.description),
    reference: indexOfAlias(HEADER_ALIASES.reference),
    account: indexOfAlias(HEADER_ALIASES.account),
  };
  // A valid header needs a date column and at least one money column.
  const hasMoney = cols.debit >= 0 || cols.credit >= 0 || cols.amount >= 0;
  return cols.date >= 0 && hasMoney ? cols : null;
}

function isSubtotalText(text: string): boolean {
  const t = text.toLowerCase();
  return SUBTOTAL_PREFIXES.some((p) => t.startsWith(p));
}

/**
 * Extracts ledger rows from a raw sheet matrix (array of rows of cells).
 * Pure — no xlsx dependency, so it is fully unit-tested.
 */
export function extractLedgerRowsFromMatrix(matrix: unknown[][]): LedgerRow[] {
  // Find the header row (first row that looks like column headers).
  const headerRowIdx = matrix.findIndex(
    (row) => matchHeader((row ?? []).map(cellToString)) !== null,
  );
  const cols =
    headerRowIdx >= 0
      ? matchHeader((matrix[headerRowIdx] ?? []).map(cellToString))
      : null;
  if (headerRowIdx === -1 || !cols) return [];

  const rows: LedgerRow[] = [];
  let currentAccount: string | undefined;

  for (let i = headerRowIdx + 1; i < matrix.length; i++) {
    const cells = (matrix[i] ?? []).map(cellToString);
    if (cells.every((c) => c === "")) continue;

    const dateIso = coerceDateToIso(matrix[i]?.[cols.date]);
    const debit = cols.debit >= 0 ? coerceMoney(matrix[i]?.[cols.debit]) : null;
    const credit =
      cols.credit >= 0 ? coerceMoney(matrix[i]?.[cols.credit]) : null;
    const amountCol =
      cols.amount >= 0 ? coerceMoney(matrix[i]?.[cols.amount]) : null;

    const debitAbs = debit != null ? Math.abs(debit) : 0;
    const creditAbs = credit != null ? Math.abs(credit) : 0;
    const amountAbs =
      debitAbs > 0
        ? debitAbs
        : creditAbs > 0
          ? creditAbs
          : Math.abs(amountCol ?? 0);

    const firstText = cells.find((c) => c !== "") ?? "";

    // A transaction row has a parseable date and a non-zero amount.
    if (dateIso && amountAbs >= 0.01) {
      const direction: "debit" | "credit" | undefined =
        debitAbs > 0 ? "debit" : creditAbs > 0 ? "credit" : undefined;
      rows.push({
        date: dateIso,
        amount: amountAbs,
        direction,
        description:
          cols.description >= 0
            ? cellToString(matrix[i]?.[cols.description]) || undefined
            : undefined,
        reference:
          cols.reference >= 0
            ? cellToString(matrix[i]?.[cols.reference]) || undefined
            : undefined,
        account:
          cols.account >= 0
            ? cellToString(matrix[i]?.[cols.account]) || currentAccount
            : currentAccount,
      });
      continue;
    }

    // No date + a leading label = either a subtotal (skip) or an account
    // section header (track it for the rows beneath).
    if (!dateIso && firstText) {
      if (isSubtotalText(firstText)) continue;
      // Treat as an account-section header only when there's no money on the
      // row (account headers carry no amount; data rows always do).
      if (amountAbs < 0.01) currentAccount = firstText;
    }
  }

  return rows;
}

/**
 * Parses a ledger spreadsheet (CSV / XLSX) buffer into ledger rows. Scans every
 * sheet and concatenates — multi-tab workbooks (one tab per account) are common.
 * Returns [] for anything it can't recognise.
 */
export function parseLedgerWorkbook(buffer: ArrayBuffer): LedgerRow[] {
  const workbook = ((): XLSX.WorkBook | null => {
    try {
      return XLSX.read(buffer, { type: "array", cellDates: true });
    } catch {
      return null;
    }
  })();
  if (!workbook) return [];

  const rows: LedgerRow[] = [];
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: true,
      defval: null,
      blankrows: false,
    });
    rows.push(...extractLedgerRowsFromMatrix(matrix));
  }
  return rows;
}
